import { Button, Col, DatePicker, Form, notification, Row, Select, Tooltip } from 'antd';
import React from 'react';
import { useIntl } from 'react-intl';
import { useMessagesFiltersQuery } from '../../invotrackQueries.generated';
import dayjs from 'dayjs';
import { CloseOutlined, SearchOutlined } from '@ant-design/icons';
import styles from '../../../info-portal/components/styling/infoportal.module.css';
import { IAppComponentProps } from '../../../../components';
import { getRangePickerLocalization } from '../../../info-portal/components/grid/CalendarUtils';
import { getDateFormatOrDefault } from '../../../users/UserFormatPreferenceServices';
import formStyles from './eimDashboardSearchForm.module.scss';

type IProps = {
    onSearch: any;
    onReset: any;
} & IAppComponentProps;

const EimDashboardSearchForm: React.FC<IProps> = (props) => {
    const { onSearch, onReset } = props;
    const intl = useIntl();
    const [form] = Form.useForm();
    const { Option } = Select;

    const { data, loading, error } = useMessagesFiltersQuery({
        onError: (queryError) => {
            notification.error({
                message: intl.formatMessage({
                    id: 'eim-dashboard-search-form-filters-error',
                    defaultMessage: 'Unable to load search filters',
                }),
                description: queryError.message,
            });
        },
    });

    return (
        <Form
            layout={'vertical'}
            onFinish={() => onSearch(form.getFieldsValue())}
            form={form}
            initialValues={{ fromDate: dayjs(), toDate: dayjs() }}
        >
            <Row gutter={20}>
                <Col span={24} style={{ paddingTop: 6 }}>
                    {/* Use CSS Grid to keep all form items on one line with equal width */}
                    <div className={styles.searchGrid}>
                        <Form.Item
                            name={'fromDate'}
                            label={intl.formatMessage({ id: 'invoiceStatusSearchForm.fromDateHint' })}
                        >
                            <DatePicker
                                className={styles.fullWidth}
                                defaultPickerValue={dayjs().startOf('month')}
                                format={getDateFormatOrDefault(props.currentUser)}
                                allowClear={true}
                                placeholder={intl.formatMessage({ id: 'invoiceStatusSearchForm.fromDateHint' })}
                                locale={getRangePickerLocalization(intl, localStorage.getItem('locale') || 'en')}
                            />
                        </Form.Item>

                        <Form.Item
                            name={'toDate'}
                            label={intl.formatMessage({ id: 'invoiceStatusSearchForm.toDateHint' })}
                        >
                            <DatePicker
                                className={styles.fullWidth}
                                defaultPickerValue={dayjs()}
                                format={getDateFormatOrDefault(props.currentUser)}
                                allowClear={true}
                                placeholder={intl.formatMessage({ id: 'invoiceStatusSearchForm.toDateHint' })}
                                locale={getRangePickerLocalization(intl, localStorage.getItem('locale') || 'en')}
                            />
                        </Form.Item>

                        <Form.Item
                            name={'company'}
                            label={intl.formatMessage({ id: 'invoiceStatusSearchForm.companyCodeHint' })}
                        >
                            <Select
                                className={styles.fullWidth}
                                mode="multiple"
                                allowClear={true}
                                loading={loading}
                                placeholder={intl.formatMessage({ id: 'invoiceStatusSearchForm.companyCodeHint' })}
                            >
                                {data?.getAllMessagesFilters?.company?.map((item) => (
                                    <Option
                                        key={`dashboard_company_code_${item.value}_${item.text}`}
                                        value={item.value}
                                    >
                                        {item.text}
                                    </Option>
                                ))}
                            </Select>
                        </Form.Item>

                        <Form.Item
                            name={'process'}
                            label={intl.formatMessage({ id: 'invoiceStatusSearchForm.processHint' })}
                        >
                            <Select
                                className={styles.fullWidth}
                                mode="multiple"
                                allowClear={true}
                                loading={loading}
                                placeholder={intl.formatMessage({ id: 'invoiceStatusSearchForm.processHint' })}
                            >
                                {data?.getAllMessagesFilters?.process?.map((item) => (
                                    <Option key={`dashboard_process_${item.value}`} value={item.value}>
                                        {item.text}
                                    </Option>
                                ))}
                            </Select>
                        </Form.Item>

                        <Form.Item
                            name={'companyGroup'}
                            label={intl.formatMessage({ id: 'invoiceStatusSearchForm.companyGroup' })}
                        >
                            <Select
                                className={styles.fullWidth}
                                allowClear={true}
                                loading={loading}
                                placeholder={intl.formatMessage({ id: 'invoiceStatusSearchForm.companyGroupHint' })}
                            >
                                {data?.getAllMessagesFilters?.group_name?.map((item) => (
                                    <Option key={`dashboard_group_${item.value}`} value={item.value}>
                                        {item.text}
                                    </Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </div>
                    <div className={formStyles.searchActions}>
                        <Tooltip title={intl.formatMessage({ id: 'info-portal-search', defaultMessage: 'Search' })}>
                            <Button
                                className={formStyles.actionBtn}
                                data-testid="info-portal-search-button"
                                type="primary"
                                htmlType="submit"
                                icon={<SearchOutlined />}
                                size={'middle'}
                            >
                                {intl.formatMessage({ id: 'info-portal-search', defaultMessage: 'Search' })}{' '}
                            </Button>
                        </Tooltip>

                        <Tooltip title={intl.formatMessage({ id: 'do-clear', defaultMessage: 'Clear' })}>
                            <Button
                                className={formStyles.actionBtn}
                                data-testid="info-portal-clear"
                                type="default"
                                icon={<CloseOutlined />}
                                onClick={() => {
                                    form.resetFields();
                                    onReset();
                                }}
                                size={'middle'}
                            >
                                {intl.formatMessage({ id: 'do-clear', defaultMessage: 'Clear' })}
                            </Button>
                        </Tooltip>
                    </div>
                </Col>
            </Row>
        </Form>
    );
};

export default EimDashboardSearchForm;
